import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import EmployeeList from './components/EmployeeList';
import EmployeeRating from './components/EmployeeRating';
import BellCurve from './components/BellCurve';
import BudgetManager from './components/BudgetManager';
import LeadershipDashboard from './components/LeadershipDashboard';
import './App.css';

const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { isAuthenticated, userRole, loading } = useAuth();

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#1a1a2e', color: '#fff' }}>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // requiredRole="leadership" means leadership OR dept_admin can access
  if (requiredRole === 'leadership' && userRole !== 'leadership' && userRole !== 'dept_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredRole && requiredRole !== 'leadership' && userRole !== requiredRole) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const MainLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div style={{ display: 'flex', height: '100vh', flexDirection: 'column' }}>
      <Header />
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <main
          style={{
            flex: 1,
            overflowY: 'auto',
            marginLeft: '250px',
            backgroundColor: '#f5f5f5',
            padding: '24px',
            transition: 'margin-left 0.3s ease',
          }}
        >
          {children}
        </main>
      </div>
      <style>
        {`
          @media (max-width: 767px) {
            main {
              margin-left: 0 !important;
            }
          }
        `}
      </style>
    </div>
  );
};

const AppContent = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Dashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employees"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EmployeeList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee-rating/:employeeId"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EmployeeRating />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bell-curve"
        element={
          <ProtectedRoute requiredRole="leadership">
            <MainLayout>
              <BellCurve />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/budget-manager"
        element={
          <ProtectedRoute requiredRole="leadership">
            <MainLayout>
              <BudgetManager />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leadership-dashboard"
        element={
          <ProtectedRoute requiredRole="leadership">
            <MainLayout>
              <LeadershipDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
